import { FilterSpecification, LayerProps } from 'shared/map-exports';
import {
  LightningNegativePolarityIcon,
  LightningPositivePolarityIcon,
} from 'features/mapping/icons/iconData/lightningIcons';
import { SELECTED_FEATURE_HIGHLIGHT_COLOR } from 'features/mapping/mapping.constants';
import {
  HEATMAP_MAX_ZOOM,
  HEATMAP_MIN_ZOOM,
  ITEM_MAX_ZOOM_PUBLIC_USER,
  ITEM_MIN_ZOOM,
  SOURCE_LAYER,
} from './LightningLayer.constants';
import { LightningTimeFilter } from './LightningLayer.types';
import { getTimeWindowBoundsSec } from './LightningLayer.utils';

export const POINT_FILTER: FilterSpecification = [
  '==',
  ['geometry-type'],
  'Point',
];

const heatmapPaint: LayerProps['paint'] = {
  'heatmap-weight': 1,
  'heatmap-intensity': [
    'interpolate',
    ['linear'],
    ['zoom'],
    HEATMAP_MIN_ZOOM,
    0.4,
    HEATMAP_MAX_ZOOM,
    2,
  ],
  'heatmap-color': [
    'interpolate',
    ['linear'],
    ['heatmap-density'],
    0,
    'rgba(0, 0, 255, 0)',
    0.2,
    'rgba(120, 100, 255, 0.4)',
    0.4,
    'rgba(200, 120, 255, 0.6)',
    0.6,
    'rgba(255, 180, 80, 0.8)',
    0.8,
    'rgba(255, 230, 80, 0.9)',
    1,
    'rgba(255, 255, 255, 1)',
  ],
  'heatmap-radius': [
    'interpolate',
    ['linear'],
    ['zoom'],
    HEATMAP_MIN_ZOOM,
    4,
    HEATMAP_MAX_ZOOM,
    30,
  ],
  'heatmap-opacity': [
    'interpolate',
    ['linear'],
    ['zoom'],
    HEATMAP_MAX_ZOOM - 1,
    1,
    HEATMAP_MAX_ZOOM,
    0,
  ],
};

const itemIconLayout: LayerProps['layout'] = {
  'icon-image': [
    'match',
    ['get', 'flash_polarity'],
    'P',
    LightningPositivePolarityIcon.name,
    LightningNegativePolarityIcon.name,
  ],
  'icon-allow-overlap': true,
  'icon-ignore-placement': true,
  'icon-offset': LightningNegativePolarityIcon.offset,
  'icon-size': [
    'interpolate',
    ['linear'],
    ['zoom'],
    ITEM_MIN_ZOOM,
    0.9,
    10,
    1,
    13,
    1.2,
  ],
};

const itemIconPaint: LayerProps['paint'] = {
  'icon-opacity': [
    'interpolate',
    ['linear'],
    ['zoom'],
    ITEM_MIN_ZOOM,
    0,
    HEATMAP_MAX_ZOOM,
    1,
  ],
};

const selectedPulsePaint: LayerProps['paint'] = {
  'circle-radius': 0, // animated by usePulseAnimation
  'circle-color': SELECTED_FEATURE_HIGHLIGHT_COLOR,
  'circle-blur': 1.25,
};

export const getTrailingWindowFilter = (
  timeFilter: LightningTimeFilter,
): FilterSpecification => {
  if (!timeFilter) return POINT_FILTER;
  if (timeFilter === 'hidden') return ['literal', false];
  const { startSec, endSec } = getTimeWindowBoundsSec(timeFilter);
  return [
    'all',
    POINT_FILTER,
    ['has', 'flash_time'],
    ['>=', ['get', 'flash_time'], startSec],
    ['<=', ['get', 'flash_time'], endSec],
  ];
};

export const getHeatmapStyle = (filter: FilterSpecification): LayerProps => ({
  type: 'heatmap',
  'source-layer': SOURCE_LAYER,
  minzoom: HEATMAP_MIN_ZOOM,
  maxzoom: HEATMAP_MAX_ZOOM,
  filter,
  paint: heatmapPaint,
});

export const getItemIconStyle = (
  filter: FilterSpecification,
  showInternalLightningLayer: boolean,
): LayerProps => ({
  type: 'symbol',
  'source-layer': SOURCE_LAYER,
  minzoom: ITEM_MIN_ZOOM,
  ...(showInternalLightningLayer ? {} : { maxzoom: ITEM_MAX_ZOOM_PUBLIC_USER }),
  filter,
  layout: itemIconLayout,
  paint: itemIconPaint,
});

export const getSelectedPulseStyle = (
  filter: FilterSpecification,
  selectedId: string,
): LayerProps => ({
  type: 'circle',
  'source-layer': SOURCE_LAYER,
  minzoom: ITEM_MIN_ZOOM,
  filter: ['all', filter, ['==', ['to-string', ['id']], selectedId]],
  paint: selectedPulsePaint,
});
